import Cookies from 'js-cookie'
import parseISO from 'date-fns/parseISO'

import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useRoutes } from 'react-router-dom'
import { SnackbarProvider } from 'notistack'

import { useTranslation } from 'react-i18next'
import { captureMessage } from '@sentry/react'

import { AuthenticatedUser } from '@probatix/common/services/toolkit/auth/auth.model'

import { AUTH_SUCCESSFUL_KEY } from '@probatix/common/services/toolkit/auth/auth.constants'

import { testsServiceActions } from '@probatix/common/services/toolkit/tests'
import { authServiceActions } from '@probatix/common/services/toolkit/auth'
import { translationsServiceActions } from '@probatix/common/services/toolkit/translations'
import LocalStorage from '@probatix/common/utils/localStorage'

import { Spinner } from '@probatix/common/components/atoms'
import { LOCALES } from '@probatix/common/translations'

import { API_MY_PROBATIX_ENTRYPOINT, SESSION_KEY } from '@probatix/checkout/config/app'

import Routing from '@probatix/checkout/core/routings'
import Meta from '@probatix/checkout/meta'

import Styled from '@probatix/checkout/styled'

import { getLocale } from '@probatix/checkout/utils'

const App = () => {
  const dispatch = useDispatch()
  const lang = navigator.language
  const { i18n } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [notFound, setNotFound] = useState<boolean>(false)
  const [authInitialized, setAuthIsInitialized] = useState<boolean>(false)
  const [transInitialized, setTransIsInitialized] = useState<boolean>(false)

  const routes = useRoutes(Routing)

  const authUser = () => {
    if (Cookies.get(AUTH_SUCCESSFUL_KEY)) {
      dispatch(authServiceActions.me({
        customEntrypoint: API_MY_PROBATIX_ENTRYPOINT,
        onErrorCallback: () => Cookies.remove(AUTH_SUCCESSFUL_KEY),
        onSuccessCallback: (response: AuthenticatedUser) => {
          dispatch(authServiceActions.setAuthData(response))
        },
      }))
    }

    setAuthIsInitialized(true)
  }

  useEffect((): void => {
    const formData = LocalStorage.get(SESSION_KEY)
    if (!formData) {
      authUser()

      return
    }

    const parsedData = JSON.parse(formData || '')
    dispatch(testsServiceActions.loadData({
      ...parsedData,
      time: parsedData.time && parseISO(parsedData.time),
    }))

    authUser()
  }, [])

  useEffect(() => {
    // App.tsx is not aware of the :locale param from the router, this is why the workaround needs to be implemented
    // to be able to determine the correct locale from the URL at the application start (to load correct translations)
    // and reduce the side effects to the user
    const locale = getLocale(pathname, i18n)
    const langCode = lang.slice(0, 2)
    const preferredLang = Object.keys(LOCALES).includes(langCode) ? langCode : 'de'
    let selectedLang = preferredLang

    if ("orders.probatix.de" === window.location.hostname) {
      window.open("https://probatix.de", "_self")

      return
    }

    if ("checkout.probatix.localhost" === window.location.hostname ||
        "kooperation.probatix.de" === window.location.hostname || 
        "kkh.probatix.de" === window.location.hostname 
    ) {
      setNotFound(true)
      navigate(`${preferredLang}/not-found`)

      return
    }

    if ('/' === window.location.pathname) {
      i18n.changeLanguage(preferredLang)
      navigate(`${preferredLang}/pick-slot`)
    } else if (locale) {
      selectedLang = locale
      i18n.changeLanguage(locale);
      (!window.location.pathname.endsWith("pick-slot")
        && captureMessage(`This url does not exist in the pharmacy project '${window.location.pathname}`, 'info')
      )

      // redirect every page to pick-slot
      navigate(`${selectedLang}/pick-slot`)
    }

    dispatch(translationsServiceActions.get({
      meta: { locale: selectedLang },
      onSuccessCallback: (res) => {
        i18n.addResourceBundle(selectedLang!, 'translation', res)
        setTransIsInitialized(true)
      },
    }))
  }, [])

  if ((!authInitialized || !transInitialized) && !notFound) {
    return <Spinner />
  }

  return (
    <SnackbarProvider 
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom',
      }}
      autoHideDuration={1500} 
      maxSnack={3} 
      variant="error"
    >
      <Styled />
      <Meta lang="de" />
      {((authInitialized && transInitialized) || (notFound)) && routes}
    </SnackbarProvider>
  )
}

export default App
